import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { PageShell } from '../common';
import type { BreadcrumbItem } from '../common/PageBreadcrumb';
import { referenciaCurta } from '../risk-applications/textosDoDestino';
import { useDadoPermitido } from '../../authorization/useListaPermitida';
import { buscarEfetivos, type AplicacaoHerdada, type Efetivos } from '../../services/efetivosService';
import { employeeService } from '../../services/employeeService';
import { occupationService } from '../../services/occupationService';
import { unitService } from '../../services/unitService';
import type { NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { AcaoEmLink } from './AcaoEmLink';
import { aplicarAosDestinos, juntarResultados, pedidosDosFalhos, type ResultadoDoDestino } from './aplicarAosDestinos';
import { destinoDoEndereco, lerEndereco, montarEndereco } from './enderecoDoAssistente';
import { FaixaDeAlcance } from './FaixaDeAlcance';
import { PainelDeIncluir } from './PainelDeIncluir';
import { ParaQuem, type EscolhaDeOnde } from './ParaQuem';
import { ancoraDaHerdada, ancoraDoItem, PainelDoDestino } from './PainelDoDestino';
import { ResultadoDaAplicacao } from './ResultadoDaAplicacao';
import { TODAS_AS_PERMISSOES, type PermissoesDaAplicacao } from './permissoesDaAplicacao';
import { ESTE_NIVEL, contar, flexionar, juntarNomes, mesmoId } from './textosDoNivel';
import type { ConfigDeAplicacao, DestinoRef, EstadoDaGravacao, ItemRecente, ResultadoDaChamada } from './tipos';

export interface InventarioDoDestinoProps<Item, A, V> {
  config: ConfigDeAplicacao<Item, A, V>;
  breadcrumbs: BreadcrumbItem[];
  /** Fora da Plataforma (D13) a tela só mostra: sem incluir, editar nem remover. */
  somenteLeitura?: boolean;
  /** O que o perfil pode fazer; a ação que ele não pode some. Sem ela, tudo. */
  permissoes?: PermissoesDaAplicacao;
}

/** Id do cadastro (string do banco, ou ausente) como número, ou nulo. */
function idOuNulo(id: number | string | null | undefined): number | null {
  return id === null || id === undefined ? null : Number(id);
}

/** Sem caminho de volta nem ficha, "Concluir" leva à lista do nível. */
const LISTA_DO_NIVEL: Record<NivelDeAplicacao, string> = {
  company: '/companies',
  unit: '/units',
  department: '/departments',
  occupation: '/occupations',
  employee: '/employees',
};

function horaDe(data: Date): string {
  return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function rolarAte(id: string) {
  document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
}

/** O item do endereço: a linha própria, ou, se ele só vem de cima, a herdada. */
function rolarAteOItem(ancora: string) {
  rolarAte(document.getElementById(ancora) ? ancora : ancoraDaHerdada(ancora));
}

/** "Mudanças nesta tabela são salvas na hora · Salvo às 10:42", acima da tabela. */
function LinhaDeGravacao({ estado }: { estado: EstadoDaGravacao | null }) {
  let situacao: React.ReactNode = null;
  if (estado?.tipo === 'salvando') situacao = <Typography component="span" variant="body2" color="text.secondary"> · Salvando…</Typography>;
  if (estado?.tipo === 'salvo')
    situacao = <Typography component="span" variant="body2" color="success.dark">{` · Salvo às ${horaDe(estado.em)}`}</Typography>;
  if (estado?.tipo === 'erro')
    situacao = (
      <Typography component="span" variant="body2" color="error.main" fontWeight={600} role="alert">
        {` · Não foi salvo: ${estado.mensagem}`}
      </Typography>
    );
  return (
    <Typography variant="body2" color="text.secondary" role="status" sx={{ mt: 2, mb: 1 }}>
      Mudanças nesta tabela são salvas na hora{situacao}
    </Typography>
  );
}

/**
 * A tela de aplicação de riscos, fontes e exames (#606, opção D): o inventário de um destino.
 * Em cima, o "Para quem?" com um seletor por nível; depois o cabeçalho do destino com o único
 * botão colorido — incluir — e a tabela com tudo o que vale ali, o próprio e o que vem de cima,
 * editável na linha. Cada rota mostra só a sua família. Cada mudança grava na hora; o que acabou
 * de mudar oferece desfazer.
 *
 * O endereço (D14) é a fonte do destino: escolher ou desfazer o destino muda o endereço, e outro
 * endereço remonta a tela. O que se escolhe acima do destino fica só na tela até ele ser escolhido.
 */
export function InventarioDoDestino<Item, A, V>(props: InventarioDoDestinoProps<Item, A, V>) {
  const { search } = useLocation();
  return <ConteudoDoInventario key={search} {...props} />;
}

function ConteudoDoInventario<Item, A, V>({
  config,
  breadcrumbs,
  somenteLeitura = false,
  permissoes = TODAS_AS_PERMISSOES,
}: InventarioDoDestinoProps<Item, A, V>) {
  const podeIncluir = !somenteLeitura && permissoes.incluir;
  const location = useLocation();
  const navigate = useNavigate();
  const [endereco] = useState(() => lerEndereco(location.search));
  const destinoDoLink = destinoDoEndereco(endereco);
  const [escolha, setEscolha] = useState<EscolhaDeOnde>(() => ({
    nivel: endereco.nivel,
    companyId: endereco.companyId,
    departmentId: endereco.departmentId,
    occupationId: endereco.occupationId,
    destino: destinoDoLink ? { ...destinoDoLink, nome: '' } : null,
  }));

  const [efetivosLidos, setEfetivosLidos] = useState<Efetivos<A> | null>(null);
  const [catalogo, setCatalogo] = useState<Item[] | null>(null);
  const [incluirAberto, setIncluirAberto] = useState(() => podeIncluir && destinoDoLink !== null && endereco.aba === 'adicionar');
  const [incluindo, setIncluindo] = useState(false);
  const [resultados, setResultados] = useState<ResultadoDoDestino<V>[] | null>(null);
  const [repetindo, setRepetindo] = useState(false);
  /** Os itens cuja cópia de cima está a caminho: o botão deles espera. */
  const [esperando, setEsperando] = useState<string[]>([]);
  const [recentes, setRecentes] = useState<ItemRecente[]>([]);
  /** O aviso da última inclusão, com os itens que ela criou: o "Desfazer" remove só eles. */
  const [incluidos, setIncluidos] = useState<{ itemIds: number[] } | null>(null);
  /** Por item recém-incluído, o id da aplicação que a inclusão criou — o painel preenche. */
  const [criadaPorItem] = useState(() => new Map<string, string>());
  const [avisoDaInclusao, setAvisoDaInclusao] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const [gravacao, setGravacao] = useState<EstadoDaGravacao | null>(null);
  /** O item até onde rolar depois da próxima leitura. */
  const rolarPara = useRef<string | null>(null);
  const primeiraLeitura = useRef(true);

  const guardarEfetivos = useCallback((lidos: Efetivos<A>) => setEfetivosLidos(lidos), []);
  const efetivos = efetivosLidos;
  const destino: DestinoRef | null = destinoDoLink && { ...destinoDoLink, nome: efetivos?.destino.nome ?? '' };
  const { singular, plural, feminino } = config.rotulos;
  const f = (palavra: string) => flexionar(feminino, palavra);

  // Depois de cada leitura: na primeira, o endereço diz até onde rolar (item, ou o que vem de
  // cima); depois de incluir, as linhas novas.
  useEffect(() => {
    if (!efetivos) return;
    if (primeiraLeitura.current) {
      primeiraLeitura.current = false;
      if (endereco.item !== null) rolarAteOItem(ancoraDoItem(config.familia, endereco.item));
      else if (endereco.aba === 'herdados') document.querySelector('[data-herdada]')?.scrollIntoView?.({ block: 'center' });
    }
    if (rolarPara.current) {
      rolarAte(rolarPara.current);
      rolarPara.current = null;
    }
  }, [efetivos, endereco, config.familia]);

  const { carregarCatalogo } = config;
  const temDestino = destino !== null;
  useEffect(() => {
    if (somenteLeitura || !temDestino || catalogo !== null) return;
    carregarCatalogo()
      .then(setCatalogo)
      .catch((erro: unknown) => {
        console.error('Erro ao carregar o catálogo:', erro);
        setCatalogo([]);
      });
  }, [somenteLeitura, temDestino, catalogo, carregarCatalogo]);

  // Onde o destino fica, para o subtítulo: o setor do cargo vem do cadastro do cargo.
  const cargoId = destinoDoLink?.nivel === 'occupation' ? destinoDoLink.id : null;
  const carregarCargo = useCallback(async () => (cargoId !== null ? occupationService.getOccupationById(cargoId) : null), [cargoId]);
  const cargo = useDadoPermitido('occupation.read', 'Setor do cargo', carregarCargo, { habilitado: cargoId !== null }).dado;
  // Funcionário vindo pelo endereço: o cargo e o setor dele vêm do cadastro do funcionário.
  const funcionarioId = destinoDoLink?.nivel === 'employee' ? destinoDoLink.id : null;
  const carregarFuncionario = useCallback(
    async () => (funcionarioId !== null ? employeeService.getEmployeeById(funcionarioId, false) : null),
    [funcionarioId]
  );
  const funcionario = useDadoPermitido('employee.read', 'Cargo do funcionário', carregarFuncionario, {
    habilitado: funcionarioId !== null,
  }).dado;
  const unidadeId = destinoDoLink?.nivel === 'unit' ? destinoDoLink.id : null;
  const carregarAlcance = useCallback(async () => (unidadeId !== null ? unitService.getAlcance(unidadeId) : null), [unidadeId]);
  const alcanceDaUnidade = useDadoPermitido('unit.read', 'Alcance da unidade', carregarAlcance, { habilitado: unidadeId !== null }).dado;

  const contexto = destino
    ? destino.nivel === 'company'
      ? 'Empresa inteira'
      : [cargo?.department?.name, efetivos?.destino.empresaNome].filter(Boolean).join(' · ')
    : '';

  const opcoesDeAplicacao = { aplicar: config.aplicar, chaveDoGrupo: config.chaveDoGrupo };
  const reler = () => setVersao((atual) => atual + 1);

  const marcarRecentes = (itemIds: number[], como: ItemRecente['como']) => {
    if (itemIds.length === 0) return;
    setRecentes((atual) => [
      ...atual.filter((recente) => !itemIds.some((id) => mesmoId(id, recente.itemId))),
      ...itemIds.map((itemId) => ({ itemId, como })),
    ]);
  };

  const avisarFalhas = (obtidos: ResultadoDoDestino<V>[]) => {
    const falhas = obtidos.flatMap((resultado) => resultado.falharam);
    const nomes = [...new Set(falhas.map((falha) => falha.nome))];
    setGravacao(
      falhas.length > 0
        ? { tipo: 'erro', mensagem: `${juntarNomes(nomes)} não ${nomes.length === 1 ? 'entrou' : 'entraram'}.` }
        : { tipo: 'salvo', em: new Date() }
    );
  };

  /**
   * "Incluir N": aplica em lote, com os valores padrão da família (documentos: sai em todos). O
   * painel fecha, as linhas novas entram no topo, em verde, e o aviso junto da tabela oferece
   * desfazer. O que já existia ou falhou aparece no resultado, com "Repetir".
   */
  const incluir = async (itens: Item[]) => {
    if (!destino || itens.length === 0) return;
    setIncluindo(true);
    setGravacao({ tipo: 'salvando' });
    const obtidos = await aplicarAosDestinos(
      [
        {
          destino,
          itens: itens.map((item) => ({
            itemId: Number(config.item.id(item)),
            nome: config.item.nome(item),
            valores: config.valoresIniciais(item, efetivos),
          })),
        },
      ],
      opcoesDeAplicacao
    );
    setIncluindo(false);
    setIncluirAberto(false);
    avisarFalhas(obtidos);
    const aplicados = obtidos.flatMap((resultado) => resultado.aplicados.map((aplicado) => aplicado.itemId));
    marcarRecentes(aplicados, 'adicionado');
    const soDeuCerto = obtidos.every((r) => r.jaExistiam.length === 0 && r.falharam.length === 0);
    setResultados(soDeuCerto ? null : obtidos);
    if (aplicados.length > 0) {
      const confira = config.rotulos.confira ?? 'Confira o que veio preenchido.';
      setIncluidos({ itemIds: aplicados });
      setAvisoDaInclusao(
        `${contar(aplicados.length, `${singular} ${f('incluído')}`, `${plural} ${f('incluídos')}`)} ${referenciaCurta('em', destino.nivel, destino.nome)}. ${confira}`
      );
      rolarPara.current = ancoraDoItem(config.familia, aplicados[aplicados.length - 1]);
    }
    reler();
  };

  /**
   * "Desfazer" da inclusão: relê e remove só as aplicações que ela criou, pelo id de cada uma. A
   * que já não existe — removida e aplicada de novo, por exemplo — fica, e o aviso diz por quê.
   */
  const desfazerInclusao = async () => {
    if (!destino || !incluidos) return;
    setAvisoDaInclusao(null);
    setGravacao({ tipo: 'salvando' });
    try {
      const lidos = await buscarEfetivos<A>(config.familia, destino.nivel, destino.id);
      const criadas: A[] = [];
      const mudaram: string[] = [];
      for (const itemId of incluidos.itemIds) {
        const criadaId = criadaPorItem.get(String(Number(itemId)));
        const atual = criadaId && lidos.proprias.find((a) => mesmoId(config.aplicacao.id(a), criadaId));
        if (atual) {
          criadas.push(atual);
          continue;
        }
        const outra = lidos.proprias.find((a) => mesmoId(config.aplicacao.itemId(a), itemId));
        if (outra) mudaram.push(config.aplicacao.nome(outra));
      }
      if (criadas.length > 0) await config.remover(destino, criadas);
      setGravacao({ tipo: 'salvo', em: new Date() });
      setRecentes((atual) => atual.filter((recente) => !incluidos.itemIds.some((id) => mesmoId(id, recente.itemId))));
      const desfeito = `Desfeito: ${contar(criadas.length, singular, plural)} ${criadas.length === 1 ? 'saiu' : 'saíram'} ${referenciaCurta('de', destino.nivel, destino.nome)}.`;
      const naoDesfeito = mudaram.length > 0 ? `${juntarNomes(mudaram)} mudou depois; não desfeito.` : '';
      setAvisoDaInclusao(criadas.length > 0 ? `${desfeito} ${naoDesfeito}`.trim() : naoDesfeito || desfeito);
    } catch (falha: unknown) {
      const motivo = falha instanceof Error && falha.message ? falha.message : 'tente de novo';
      setGravacao({ tipo: 'erro', mensagem: `não foi possível desfazer: ${motivo}` });
    }
    setIncluidos(null);
    reler();
  };

  /**
   * "Mudar só para o <destino>": a aplicação própria nasce copiada da que vem de cima, com tudo
   * o que ela tem, e a linha vira editável no lugar, com borda azul.
   */
  const ajustar = async (herdada: AplicacaoHerdada<A>) => {
    if (!destino) return;
    const itemId = Number(config.aplicacao.itemId(herdada));
    const chave = String(itemId);
    if (esperando.includes(chave)) return;
    const nome = config.aplicacao.nome(herdada);
    setEsperando((atual) => [...atual, chave]);
    setGravacao({ tipo: 'salvando' });
    let resposta: ResultadoDaChamada;
    try {
      resposta = await config.copiarDoHerdado(destino, herdada);
    } catch (erro: unknown) {
      const motivo = erro instanceof Error && erro.message ? erro.message : 'não foi possível copiar';
      resposta = { aplicados: [], jaExistiam: [], falharam: [{ itemId, motivo }] };
    }
    setEsperando((atual) => atual.filter((id) => id !== chave));
    const [falha] = resposta.falharam;
    if (falha) {
      setGravacao({ tipo: 'erro', mensagem: `${nome} não mudou: ${falha.motivo}` });
    } else if (resposta.jaExistiam.length > 0) {
      setGravacao({ tipo: 'erro', mensagem: `${nome} já era ${f('próprio')} ${referenciaCurta('de', destino.nivel, destino.nome)}.` });
    } else {
      setGravacao({ tipo: 'salvo', em: new Date() });
      marcarRecentes([itemId], 'ajustado');
    }
    reler();
  };

  const repetirFalhos = async () => {
    if (!resultados) return;
    setRepetindo(true);
    setGravacao({ tipo: 'salvando' });
    const repeticao = await aplicarAosDestinos(pedidosDosFalhos(resultados), opcoesDeAplicacao);
    setResultados(juntarResultados(resultados, repeticao));
    setRepetindo(false);
    avisarFalhas(repeticao);
    marcarRecentes(
      repeticao.flatMap((resultado) => resultado.aplicados.map((aplicado) => aplicado.itemId)),
      'adicionado'
    );
    reler();
  };

  const concluir = () => {
    const ficha = destino ? (config.linkDaFicha?.(destino) ?? null) : null;
    navigate(endereco.voltar ?? ficha ?? (destino ? LISTA_DO_NIVEL[destino.nivel] : '/'));
  };

  // Com o destino vindo pelo endereço, o que o link não trouxe acima dele vem do que já foi lido:
  // a empresa dos efetivos, o setor do cadastro do cargo, o cargo e o setor do funcionário.
  const escolhaVisivel: EscolhaDeOnde = destinoDoLink
    ? {
        ...escolha,
        companyId: escolha.companyId ?? (efetivos ? Number(efetivos.destino.empresaId) : null),
        departmentId: escolha.departmentId ?? idOuNulo(cargo?.departmentId ?? funcionario?.departmentId),
        occupationId: escolha.occupationId ?? idOuNulo(funcionario?.occupationId),
      }
    : escolha;

  /** Escolher ou desfazer o destino vai para o endereço; o que fica acima dele muda só na tela. */
  const mudarEscolha = (nova: EscolhaDeOnde) => {
    if (!nova.destino && !destinoDoLink) {
      setEscolha(nova);
      return;
    }
    const ids: Partial<Record<NivelDeAplicacao, number | null>> = {
      company: nova.companyId,
      department: nova.departmentId,
      occupation: nova.occupationId,
    };
    // Sem destino, o id do próprio nível sairia como destino escolhido: fica de fora.
    if (!nova.destino && nova.nivel) ids[nova.nivel] = null;
    navigate(
      montarEndereco(location.pathname, {
        nivel: nova.nivel ?? undefined,
        ids: nova.destino ? { ...ids, [nova.destino.nivel]: nova.destino.id } : ids,
        aba: nova.destino ? 'aplicados' : undefined,
        voltar: endereco.voltar ?? undefined,
      })
    );
  };

  const destaque = config.destaque;
  const comDestaque = efetivos && destaque
    ? [...efetivos.proprias, ...efetivos.herdadas.filter((h) => !h.substituidaNoDestino)].filter(destaque.aceita)
    : [];
  const [primeiroDestaque] = comDestaque;

  const doNivel = destino ? ESTE_NIVEL[destino.nivel].replace(/^est/, 'nest') : '';
  const subtitulo = [contexto, efetivos ? contar(efetivos.alcance.funcionarios, 'funcionário', 'funcionários') : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={config.rotulos.titulo}
      subtitle={`${f('Os')} ${plural} de cada cargo, setor ou unidade.`}
    >
      <Card>
        <CardContent sx={{ p: 3 }}>
          <ParaQuem escolha={escolhaVisivel} aoMudar={mudarEscolha} />

          {!destino && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              {`Escolha o nível e, nele, para quem. Aqui aparecem ${f('os')} ${plural} que valem ali, de onde ${feminino ? 'elas vêm' : 'eles vêm'} e em que documentos saem.`}
            </Typography>
          )}

          {destino && (
            <>
              <Divider sx={{ my: 3 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} justifyContent="space-between">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" component="h2">
                      {`${config.rotulos.daTela} de: ${destino.nome || '…'}`}
                    </Typography>
                    {destaque && primeiroDestaque && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={destaque.rotulo(comDestaque.length)}
                        onClick={() => rolarAte(ancoraDoItem(config.familia, config.aplicacao.itemId(primeiroDestaque)))}
                      />
                    )}
                  </Stack>
                  {subtitulo && (
                    <Typography variant="body2" color="text.secondary">
                      {subtitulo}
                    </Typography>
                  )}
                </Box>
                {podeIncluir && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIncluirAberto(true)} sx={{ flexShrink: 0 }}>
                    {`Incluir ${plural} ${doNivel}`}
                  </Button>
                )}
              </Stack>

              {alcanceDaUnidade && (
                <Box sx={{ mt: 2 }}>
                  <FaixaDeAlcance alcance={alcanceDaUnidade} />
                </Box>
              )}

              {somenteLeitura ? <Box sx={{ mt: 2 }} /> : <LinhaDeGravacao estado={gravacao} />}

              <PainelDoDestino
                config={config}
                destino={destino}
                somenteLeitura={somenteLeitura}
                permissoes={permissoes}
                itemDestacado={endereco.item}
                aoCarregar={guardarEfetivos}
                linkDaOrigem={
                  somenteLeitura
                    ? undefined
                    : (herdada) =>
                        montarEndereco(location.pathname, {
                          nivel: herdada.origem.nivel,
                          ids: { [herdada.origem.nivel]: herdada.origem.id },
                          aba: 'aplicados',
                          voltar: endereco.voltar ?? undefined,
                        })
                }
                acimaDaTabela={
                  <>
                    {resultados && resultados.length > 0 && (
                      <ResultadoDaAplicacao
                        resultados={resultados}
                        rotulos={config.rotulos}
                        linkDaFicha={config.linkDaFicha}
                        repetindo={repetindo}
                        aoRepetirFalhos={() => void repetirFalhos()}
                      />
                    )}
                    {avisoDaInclusao && (
                      <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => setAvisoDaInclusao(null)}
                        action={
                          incluidos && podeIncluir ? (
                            <AcaoEmLink cor="inherit" onClick={() => void desfazerInclusao()}>
                              Desfazer
                            </AcaoEmLink>
                          ) : undefined
                        }
                      >
                        {avisoDaInclusao}
                      </Alert>
                    )}
                  </>
                }
                recentes={recentes}
                registroDeCriadas={criadaPorItem}
                aoDeixarDeSerRecente={(itemId) => setRecentes((atual) => atual.filter((r) => !mesmoId(r.itemId, itemId)))}
                aoAjustar={(herdada) => void ajustar(herdada)}
                esperando={esperando}
                versao={versao}
                aoMudarGravacao={setGravacao}
              />

              {podeIncluir && (
                <PainelDeIncluir
                  config={config}
                  aberto={incluirAberto}
                  catalogo={catalogo}
                  destino={destino}
                  efetivos={efetivos}
                  empresaId={efetivos?.destino.empresaId ?? endereco.companyId}
                  incluindo={incluindo}
                  aoFechar={() => setIncluirAberto(false)}
                  aoIncluir={(itens) => void incluir(itens)}
                />
              )}
            </>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={concluir} disabled={incluindo || repetindo || esperando.length > 0}>
              Concluir
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </PageShell>
  );
}
